import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import './category.css';
import './editor.css';

const CategoriesPage = () => {
  const [aboutText, setAboutText] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    whatsapp: ''
  });

  const queryClient = useQueryClient();
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const { data: siteSettings, isLoading, error, refetch } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/about`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch site settings:', errorData);
        throw new Error(errorData.message || 'Failed to fetch site settings');
      }
      
      const data = await response.json();
     
      return data;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (siteSettings) {
   
      setAboutText(siteSettings.about_section || '');
      setSocialLinks({
        facebook: siteSettings.facebook || '',
        twitter: siteSettings.twitter || '',
        instagram: siteSettings.instagram || '',
        whatsapp: siteSettings.whatsapp || ''
      });
    }
  }, [siteSettings]);

  const handleLoadSettings = () => {
    refetch();
  };

  // Separate mutations for different update types
  const updateWithFilesMutation = useMutation({
    mutationFn: async (formData) => {

      
      const response = await fetch(`${API_BASE_URL}/api/about`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Update failed:', errorData);
        throw new Error(errorData.message || 'Failed to update');
      }
      
      const result = await response.json();
    
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['siteSettings']);
      alert('تم التحديث بنجاح');
      
      setLogoFile(null);
      setBannerFile(null);
      
      const logoInput = document.querySelector('input[type="file"]:first-of-type');
      const bannerInput = document.querySelector('input[type="file"]:last-of-type');
      if (logoInput) logoInput.value = '';
      if (bannerInput) bannerInput.value = '';
    },
    onError: (error) => {
      console.error('❌ Update error:', error);
      alert(`حدث خطأ أثناء التحديث: ${error.message}`);
    }
  });

  const updateWithoutFilesMutation = useMutation({
    mutationFn: async (jsonData) => {
   
      
      const response = await fetch(`${API_BASE_URL}/api/about`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(jsonData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Update failed:', errorData);
        throw new Error(errorData.message || 'Failed to update');
      }
      
      const result = await response.json();
     
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['siteSettings']);
      alert('تم التحديث بنجاح');
      
      // Force refetch to get latest data
      setTimeout(() => {
        refetch();
      }, 500);
    },
    onError: (error) => {
      console.error('❌ Update error:', error);
      alert(`حدث خطأ أثناء التحديث: ${error.message}`);
    }
  });

  const handleUpdateAbout = () => {
    if (!aboutText.trim()) {
      alert('يرجى إدخال نص نبذة عن الموقع');
      return;
    }
    
    const data = { about_section: aboutText };
    updateWithoutFilesMutation.mutate(data);
  };

  const handleUpdateLogo = () => {
    if (!logoFile) {
      alert('يرجى اختيار ملف الشعار');
      return;
    }
    
    const formData = new FormData();
    formData.append('logo', logoFile);
    updateWithFilesMutation.mutate(formData);
  };

  const handleUpdateBanner = () => {
    if (!bannerFile) {
      alert('يرجى اختيار ملف البانر');
      return;
    }
    
    const formData = new FormData();
    formData.append('banner', bannerFile);
    updateWithFilesMutation.mutate(formData);
  };

  const handleUpdateSocialLinks = () => {
   
    
    const data = {
      facebook: socialLinks.facebook || '',
      twitter: socialLinks.twitter || '',
      instagram: socialLinks.instagram || '',
      whatsapp: socialLinks.whatsapp || ''
    };
    
    updateWithoutFilesMutation.mutate(data);
  };

  const handleUpdateAll = () => {
    const formData = new FormData();
    
    formData.append('about_section', aboutText || '');
    formData.append('facebook', socialLinks.facebook || '');
    formData.append('twitter', socialLinks.twitter || '');
    formData.append('instagram', socialLinks.instagram || '');
    formData.append('whatsapp', socialLinks.whatsapp || '');
    
    if (logoFile) {
      formData.append('logo', logoFile);
    }
    if (bannerFile) {
      formData.append('banner', bannerFile);
    }

    updateWithFilesMutation.mutate(formData);
  };

  const handleSocialLinkChange = (platform, value) => {
   
    setSocialLinks(prev => {
      const newLinks = {
        ...prev,
        [platform]: value
      };
    
      return newLinks;
    });
  };

  const isUpdating = updateWithFilesMutation.isPending || updateWithoutFilesMutation.isPending;

  if (error) {
    return (
      <div className="error-container" style={{ padding: '2rem', textAlign: 'center' }}>
        <h3 style={{ color: 'red' }}>خطأ في تحميل البيانات</h3>
        <p>{error.message}</p>
        <button 
          onClick={handleLoadSettings}
          style={{ 
            padding: '0.5rem 1rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>إدارة إعدادات الموقع</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
         
        </div>
      </div>

      {isLoading && (
        <div className="loading-container" style={{ 
          minHeight: '100px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          margin: '1rem 0'
        }}>
          <div className="loading-spinner">جارِ تحميل الإعدادات...</div>
        </div>
      )}

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3>نبذة عن الموقع</h3>
        </div>
        <div className="card-body">
          <textarea
            value={aboutText}
            onChange={(e) => {
              setAboutText(e.target.value);
            }}
            placeholder="أدخل نبذة عن الموقع..."
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />

          <button 
            className="update-btn"
            onClick={handleUpdateAbout}
            disabled={isUpdating}
            style={{ marginTop: '1rem' }}
          >
            {isUpdating ? 'جارِ التحديث...' : 'تحديث النبذة'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3>الشعار</h3>
        </div>
        <div className="card-body">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              setLogoFile(file);
            }}
            style={{
              display: 'block',
              padding: '0.5rem 0',
              marginBottom: '1rem'
            }}
          />
          {logoFile && (
            <div className="file-preview" style={{ 
              padding: '0.5rem',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <span>الملف المحدد: {logoFile.name}</span>
            </div>
          )}
          {siteSettings?.logo && (
            <div className="current-image" style={{ marginBottom: '1rem' }}>
              <span style={{ display: 'block', marginBottom: '0.5rem' }}>الصورة الحالية:</span>
              <img 
              src={`${API_BASE_URL}${siteSettings.logo}`}
              alt="Current Logo" 
              style={{ 
                maxWidth: '150px', 
                maxHeight: '150px', 
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            </div>
          )}
          <button 
            className="update-btn"
            onClick={handleUpdateLogo}
            disabled={isUpdating || !logoFile}
          >
            {isUpdating ? 'جارِ التحديث...' : 'تحديث الشعار'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3>البانر</h3>
        </div>
        <div className="card-body">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              setBannerFile(file);
            }}
            style={{
              display: 'block',
              padding: '0.5rem 0',
              marginBottom: '1rem'
            }}
          />
          {bannerFile && (
            <div className="file-preview" style={{ 
              padding: '0.5rem',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <span>الملف المحدد: {bannerFile.name}</span>
            </div>
          )}
          {siteSettings?.banner && (
            <div className="current-image" style={{ marginBottom: '1rem' }}>
              <span style={{ display: 'block', marginBottom: '0.5rem' }}>الصورة الحالية:</span>
              <img 
                src={`${API_BASE_URL}${siteSettings.banner}`}
                alt="Current Banner" 
                style={{ 
                  maxWidth: '300px', 
                  maxHeight: '150px', 
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <button 
            className="update-btn"
            onClick={handleUpdateBanner}
            disabled={isUpdating || !bannerFile}
          >
            {isUpdating ? 'جارِ التحديث...' : 'تحديث البانر'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3>روابط التواصل الاجتماعي</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '0.75rem' }}>
          <input
            type="url"
            placeholder="رابط فيسبوك..."
            value={socialLinks.facebook}
            onChange={(e) => {
              handleSocialLinkChange('facebook', e.target.value);
            }}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          />

          <input
            type="url"
            placeholder="رابط تويتر..."
            value={socialLinks.twitter}
            onChange={(e) => {
              handleSocialLinkChange('twitter', e.target.value);
            }}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          />
        
          <input
            type="url"
            placeholder="رابط إنستغرام..."
            value={socialLinks.instagram}
            onChange={(e) => {
              handleSocialLinkChange('instagram', e.target.value);
            }}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          />

          <input
            type="text"
            placeholder="رقم الواتساب..."
            value={socialLinks.whatsapp}
            onChange={(e) => {
              handleSocialLinkChange('whatsapp', e.target.value);
            }}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          />
          <button 
            className="update-btn"   
            onClick={handleUpdateSocialLinks}
            disabled={isUpdating}
            style={{ marginTop: '0.5rem' }}
          >
            {isUpdating ? 'جارِ التحديث...' : 'تحديث الروابط'}
          </button>
        </div>
      </div>

    </div>
  );
};

export default CategoriesPage;